import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {ResidueType} from 'biojs/BioConstants';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Modal from 'ui-library/modals/Modal';
import strUtil from 'util/strUtil';

interface FeatureSyntaxHelpModalProps {
  residueType: ResidueType;
  onRequestClose: () => void;
}

interface SequenceFragment {
  fragment: string;
  matched: boolean;
}
interface SyntaxExampleProps {
  feature: string;
  sequence: Array<SequenceFragment>;
}

const nucleotideExamples: Array<SyntaxExampleProps> = [
  {
    feature: 'AATT',
    sequence: [
      {fragment: 'AA', matched: false},
      {fragment: 'AATT', matched: true},
      {fragment: 'TTGGGGGCCCCCAAGT', matched: false},
    ],
  },
  {
    feature: 'aAtT',
    sequence: [
      {fragment: 'AA', matched: false},
      {fragment: 'AATT', matched: true},
      {fragment: 'TTGGGGGCCCCCAAGT', matched: false},
    ],
  },
  {
    feature: 'ANT',
    sequence: [
      {fragment: 'AA', matched: false},
      {fragment: 'AAT', matched: true},
      {fragment: 'TTTGGGGGCCCCCA', matched: false},
      {fragment: 'AGT', matched: true},
    ],
  },
  {
    feature: 'T#C',
    sequence: [
      {fragment: 'AAAATTT', matched: false},
      {fragment: 'TGGGGGC', matched: true},
      {fragment: 'CCCCAAGT', matched: false},
    ],
  },
  {
    feature: 'T(GGGG)GC',
    sequence: [
      {fragment: 'AAAATTT', matched: false},
      {fragment: 'T', matched: true},
      {fragment: 'GGGG', matched: false},
      {fragment: 'GC', matched: true},
      {fragment: 'CCCCAAGT', matched: false},
    ],
  },
  {
    feature: 'T(#)CC',
    sequence: [
      {fragment: 'AAAATTT', matched: false},
      {fragment: 'T', matched: true},
      {fragment: 'GGGGG', matched: false},
      {fragment: 'CC', matched: true},
      {fragment: 'CCCAAGT', matched: false},
    ],
  },
  {
    feature: 'CA<ATT>TTGAG',
    sequence: [
      {fragment: 'AA', matched: false},
      {fragment: 'AATTTTG', matched: true},
      {fragment: 'GGGGCCCCCAAGT', matched: false},
    ],
  },
  {
    feature: 'CA<ATTT',
    sequence: [
      {fragment: 'AA', matched: false},
      {fragment: 'AATTT', matched: true},
      {fragment: 'TGGGGGCCCCCAAGT', matched: false},
    ],
  },
];

const aaExamples: Array<SyntaxExampleProps> = [
  {
    feature: 'ARD',
    sequence: [
      {fragment: 'KK', matched: false},
      {fragment: 'ARD', matched: true},
      {fragment: 'QARS', matched: false},
    ],
  },
  {
    feature: 'aRD',
    sequence: [
      {fragment: 'Kk', matched: false},
      {fragment: 'ArD', matched: true},
      {fragment: 'QARS', matched: false},
    ],
  },
  {
    feature: 'ARD',
    sequence: [
      {fragment: 'AA', matched: false},
      {fragment: 'ARD', matched: true},
      {fragment: 'QASRK', matched: false},
      {fragment: 'ARD', matched: true},
    ],
  },
  {
    feature: 'A#D',
    sequence: [
      {fragment: 'FYQND', matched: false},
      {fragment: 'AKHRED', matched: true},
      {fragment: 'QQW', matched: false},
    ],
  },
  {
    feature: 'A(QQQQQ)RD',
    sequence: [
      {fragment: 'TSNNN', matched: false},
      {fragment: 'A', matched: true},
      {fragment: 'QQQQQ', matched: false},
      {fragment: 'RD', matched: true},
      {fragment: 'UGUG', matched: false},
    ],
  },
  {
    feature: 'T(#)RR',
    sequence: [
      {fragment: 'AAAAUUU', matched: false},
      {fragment: 'T', matched: true},
      {fragment: 'QKQD', matched: false},
      {fragment: 'RR', matched: true},
      {fragment: 'UGP', matched: false},
    ],
  },
  {
    feature: 'QQ<ARD>NNIL',
    sequence: [
      {fragment: 'AA', matched: false},
      {fragment: 'QARDNNI', matched: true},
      {fragment: 'QATKW', matched: false},
    ],
  },
  {
    feature: 'HA<ATTT',
    sequence: [
      {fragment: 'AA', matched: false},
      {fragment: 'AATTT', matched: true},
      {fragment: 'QQIQLLLI', matched: false},
    ],
  },
];

function SyntaxExample({feature, sequence}: SyntaxExampleProps): JSX.Element {
  const fragments = sequence.map(({fragment, matched}, i) => (
    <span key={i} className={matched ? css(styles.matches) : undefined}>
      {fragment}
    </span>
  ));
  return (
    <>
      <dt className={css(styles.exampleFeature)}>{feature}</dt>
      <dd className={css(styles.exampleMatch)}>{fragments}</dd>
    </>
  );
}

export default function FeatureSyntaxHelpModal({
  residueType,
  onRequestClose,
}: FeatureSyntaxHelpModalProps): JSX.Element {
  const examples = residueType === ResidueType.AA ? aaExamples : nucleotideExamples;
  const sequenceCharWord = residueType === ResidueType.AA ? 'residue' : 'base';

  return (
    <Modal title="Feature syntax" buttons={[{cancel: true, text: 'Close'}]} onRequestClose={onRequestClose}>
      <div id="featureLibraryHelpModal">
        <div className="col-md-12">
          <p>
            {`Features are case-insensitive - upper case and lower case letters are treated the same. In
            addition to the standard ${residueType === ResidueType.AA ? 'amino acids' : 'IUPAC bases'},
            the following characters are also allowed:`}
          </p>
          <dl className="dl-horizontal">
            <dt className={css(styles.symbols)}>#</dt>
            <dd className={css(styles.definitions)}>
              {`Any arbitrary string of zero or more ${sequenceCharWord}s can be matched here.`}
            </dd>
            <dt className={css(styles.symbols)}>&lt;, &gt;</dt>
            <dd className={css(styles.definitions)}>
              {`Any ${sequenceCharWord}s before a < or after a > are considered optional, and are
              matched after the in-between sequence has been found.`}
            </dd>
            <dt className={css(styles.symbols)}>()</dt>
            <dd className={css(styles.definitions)}>
              {`${strUtil.toSentenceCase(sequenceCharWord)}s in between parentheses are matched, but not included in the final
               sequence annotation.`}
            </dd>
          </dl>
          <hr />
          <dl className={classNames('dl-horizontal title', css(styles.tableHeader))}>
            <dt className={css(styles.featureHeader)}>Feature</dt>
            <dd className={css(styles.descriptionHeader)}>
              Sequence (matches in <span className={css(styles.matches)}>red</span>)
            </dd>
          </dl>
          <dl className="dl-horizontal">
            {examples.map(({feature, sequence}, index) => (
              <SyntaxExample key={index} feature={feature} sequence={sequence} />
            ))}
          </dl>
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  symbols: {
    width: '50px',
  },
  definitions: {
    marginLeft: '70px',
    marginBottom: '10px',
    fontWeight: 400,
  },
  tableHeader: {
    fontSize: '15px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    margin: '0px',
    padding: '0px',
  },
  featureHeader: {
    width: '100px',
    marginBottom: '0px',
  },
  descriptionHeader: {
    marginLeft: '120px',
    marginBottom: '0px',
  },
  matches: {
    padding: '0px',
    margin: '0px',
    backgroundColor: 'red',
  },
  exampleFeature: {
    width: '100px',
  },
  exampleMatch: {
    marginLeft: '120px',
    marginBottom: '10px',
    fontWeight: 400,
  },
});
